import {
  type DocumentPickerAsset,
  type DocumentPickerOptions,
  getDocumentAsync,
} from "expo-document-picker";
import { useCallback, useState } from "react";

import { acceptedUploadMimeTypes } from "@medbillai/utils/files";

import {
  type PickerNoResponseInfo,
  startPickerNoResponseTimer,
} from "./pickerNoResponse";

export type UseFilePickerOptions = {
  /**
   * Fires once the user finishes selecting files. Return a `Promise` to keep
   * `isLoading` true until your upload work settles; the hook awaits it inside
   * its own `try/finally` and clears loading when the promise resolves.
   */
  onFilesSelected: (files: DocumentPickerAsset[]) => Promise<void> | void;
  /** MIME types to allow. Defaults to PDF + JPEG/PNG. */
  accept?: DocumentPickerOptions["type"];
  /** Allow selecting more than one file. Defaults to true. */
  multiple?: boolean;
  /** The user dismissed the picker without choosing anything. */
  onCanceled?: () => void;
  /** The picker threw. Without this the failure is only a console line. */
  onError?: (error: unknown) => void;
  /** The picker has not answered yet; see PICKER_NO_RESPONSE_TIMEOUT_MS. */
  onNoResponse?: (info: PickerNoResponseInfo) => void;
};

export type UseFilePickerResult = {
  /** Opens the OS document picker; resolves once the picker is dismissed. */
  pickFile: () => Promise<void>;
  /** True from `pickFile` invocation until `onFilesSelected` settles. */
  isLoading: boolean;
};

// Derived, not restated: the drop zone beside this picker tests files with
// `isAcceptedUploadFile`, and a hand-kept list here would eventually offer a
// different set of files than the one that zone accepts.
const defaultAccept: DocumentPickerOptions["type"] = acceptedUploadMimeTypes();

/**
 * Wraps `expo-document-picker`'s `getDocumentAsync`. Owns its own `isLoading`
 * state and awaits the consumer's `onFilesSelected` callback so the loading
 * affordance stays up while uploads complete.
 */
export const useFilePicker = ({
  onFilesSelected,
  accept = defaultAccept,
  multiple = true,
  onCanceled,
  onError,
  onNoResponse,
}: UseFilePickerOptions): UseFilePickerResult => {
  const [isLoading, setIsLoading] = useState(false);

  const pickFile = useCallback(async () => {
    setIsLoading(true);
    const cancelNoResponseTimer = startPickerNoResponseTimer(onNoResponse);
    try {
      const result = await getDocumentAsync({
        type: accept,
        multiple,
      });
      // Cleared on the picker's answer, not on `onFilesSelected` settling —
      // the consumer's upload work can legitimately outrun the timeout.
      cancelNoResponseTimer();
      if (result.canceled) {
        onCanceled?.();
        return;
      }
      await onFilesSelected(result.assets);
    } catch (error) {
      cancelNoResponseTimer();
      console.error("Error picking document", error);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [accept, multiple, onFilesSelected, onCanceled, onError, onNoResponse]);

  return { pickFile, isLoading };
};
